import { ChangeEvent } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Checkbox, FormGroup, FormControlLabel, SxProps, Theme } from '@mui/material';

const resolveSxOverrides = (sx: SxProps<Theme> | undefined, theme: Theme): SxProps<Theme> => {
  return typeof sx === 'function' ? sx(theme) : (sx ?? {});
};

export type CheckboxGroupProps = Readonly<{
  options: {
    label: string;
    value: string;
    disabled?: boolean;
  }[];
  selectedValues?: string[];
  setSelectedValues?: (value: string[]) => void;
  disabled?: boolean;
  defaultChecked?: boolean;
  maxSelectedOptions?: number;
  sxOverrides?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  /** Scopes the per-option checkbox ids as `${id}-${option.value}`. */
  id?: string;
}>;

export const CheckboxGroup = ({
  selectedValues,
  setSelectedValues,
  options,
  disabled,
  defaultChecked,
  maxSelectedOptions,
  sxOverrides,
  containerSx,
  id,
}: CheckboxGroupProps) => {
  const formControl: SxProps<Theme> = (theme) => ({
    ...resolveSxOverrides(sxOverrides, theme),
  });

  const handleChange = (option: string) => (event: ChangeEvent<HTMLInputElement>) => {
    if (!setSelectedValues || !selectedValues) return;

    const isChecking = event.target.checked;

    // If unchecking, always allow it
    if (!isChecking) {
      const updatedValues = selectedValues.filter((value) => value !== option);

      setSelectedValues(updatedValues);

      return;
    }

    // If checking and we have a max limit, check if we can add more
    if (isChecking && maxSelectedOptions && selectedValues.length >= maxSelectedOptions) {
      return; // Don't allow checking if we've reached the limit
    }

    // If checking and we're under the limit, add the option
    if (isChecking) {
      const updatedValues = [...selectedValues, option];

      setSelectedValues(updatedValues);
    }
  };

  const isCheckboxDisabled = (isSelected: boolean, optionDisabled?: boolean) => {
    return (
      disabled ||
      optionDisabled ||
      (maxSelectedOptions &&
        selectedValues &&
        selectedValues.length >= maxSelectedOptions &&
        !isSelected)
    );
  };

  return (
    <FormGroup sx={containerSx} id={id}>
      {options.map((option) => {
        const isSelected = selectedValues?.includes(option.value) ?? false;
        const isDisabled = isCheckboxDisabled(isSelected, option.disabled);

        return (
          <FormControlLabel
            key={option.value}
            sx={formControl}
            checked={defaultChecked || isSelected}
            disabled={!!isDisabled}
            control={
              <Checkbox
                id={id ? `${id}-${option.value}` : `checkbox-${option.value}`}
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckIcon />}
                onChange={handleChange(option.value)}
                disabled={!!isDisabled}
                defaultChecked={defaultChecked}
              />
            }
            label={option.label}
          />
        );
      })}
    </FormGroup>
  );
};
